import {
  Paper,
  Stack,
  Group,
  Text,
  Autocomplete,
  Divider,
  Badge,
  Button,
  ActionIcon,
  NumberInput,
  Switch,
  SegmentedControl,
  Box,
  Tooltip,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconPlus, IconTrash, IconPercentage, IconHelpCircle } from '@tabler/icons-react';
import { AssetBundleItem } from '@/API';
import {
  ExtensionManifestFormValues,
  StateGroupConfig,
  WeightedActionItem,
  RandomSelectionStrategy,
  calculateNormalizedWeights,
} from './types';
import { AssetBundleFileSelect } from './AssetBundleFileSelect';

interface SpineTemplateFormProps {
  form: UseFormReturnType<ExtensionManifestFormValues>;
  skeletonFiles: string[];
  atlasFiles: string[];
  availableSpineAnimations: string[];
  workspaceId: string;
  botProviderId: string;
  bundle: AssetBundleItem;
  onBundleUpdated?: (updatedBundle: AssetBundleItem) => void;
}

type SpineStateKey =
  | 'spineIdleConfig'
  | 'spineSleepConfig'
  | 'spineThinkingConfig'
  | 'spineSpeakingConfig'
  | 'spineErrorConfig'
  | 'spineClickConfig';

interface StateMeta {
  key: SpineStateKey;
  label: string;
  defaultAnim: string;
  defaultLoop: boolean;
  defaultStrategy: RandomSelectionStrategy;
}

const SPINE_STATES: StateMeta[] = [
  { key: 'spineIdleConfig', label: '待機動作 (Idle)', defaultAnim: 'idle', defaultLoop: true, defaultStrategy: 'on-complete' },
  { key: 'spineSleepConfig', label: '休眠動作 (Sleep)', defaultAnim: 'sleep', defaultLoop: true, defaultStrategy: 'on-complete' },
  { key: 'spineThinkingConfig', label: '思考中動作 (Thinking)', defaultAnim: 'think', defaultLoop: true, defaultStrategy: 'on-complete' },
  { key: 'spineSpeakingConfig', label: '說話動作 (Speaking)', defaultAnim: 'talk', defaultLoop: true, defaultStrategy: 'on-complete' },
  { key: 'spineErrorConfig', label: '錯誤動作 (Error)', defaultAnim: 'confused', defaultLoop: true, defaultStrategy: 'on-complete' },
  { key: 'spineClickConfig', label: '點擊互動動作 (Click/Touch)', defaultAnim: 'wave', defaultLoop: false, defaultStrategy: 'on-state-enter' },
];

export function SpineTemplateForm({
  form,
  skeletonFiles,
  atlasFiles,
  availableSpineAnimations,
  workspaceId,
  botProviderId,
  bundle,
  onBundleUpdated,
}: SpineTemplateFormProps) {
  const handleAddItem = (stateKey: SpineStateKey, defaultAnim: string, defaultLoop: boolean) => {
    const current = form.values[stateKey] as StateGroupConfig;
    const items = [...(current.items || [])];
    items.push({
      animation: defaultAnim,
      weight: 1,
      loop: defaultLoop,
    });
    form.setFieldValue(`${stateKey}.items`, items);
  };

  const handleRemoveItem = (stateKey: SpineStateKey, index: number) => {
    const current = form.values[stateKey] as StateGroupConfig;
    const items = current.items.filter((_, i) => i !== index);
    form.setFieldValue(`${stateKey}.items`, items);
  };

  const handleUpdateItem = (
    stateKey: SpineStateKey,
    index: number,
    patch: Partial<WeightedActionItem>
  ) => {
    const current = form.values[stateKey] as StateGroupConfig;
    const items = current.items.map((item, i) => (i === index ? { ...item, ...patch } : item));
    form.setFieldValue(`${stateKey}.items`, items);
  };

  const handleStrategyChange = (stateKey: SpineStateKey, strategy: RandomSelectionStrategy) => {
    form.setFieldValue(`${stateKey}.strategy`, strategy);
  };

  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Text fw={500} size="sm">
            Spine 檔案與動作對應設定
          </Text>
          {availableSpineAnimations.length > 0 && (
            <Badge size="xs" color="blue" variant="light">
              已偵測 {availableSpineAnimations.length} 個動作 (可自選或輸入)
            </Badge>
          )}
        </Group>

        <Group grow>
          <AssetBundleFileSelect
            label="骨骼檔 (Skeleton .json / .skel)"
            placeholder="選擇骨骼檔"
            data={skeletonFiles}
            withAsterisk
            workspaceId={workspaceId}
            botProviderId={botProviderId}
            bundle={bundle}
            allowedExtensions={['.json', '.skel']}
            pickerTitle="挑選骨骼檔 (.json / .skel)"
            onBundleUpdated={onBundleUpdated}
            {...form.getInputProps('skeletonFile')}
          />
          <AssetBundleFileSelect
            label="圖集檔 (Atlas .atlas)"
            placeholder="選擇圖集檔"
            data={atlasFiles}
            withAsterisk
            workspaceId={workspaceId}
            botProviderId={botProviderId}
            bundle={bundle}
            allowedExtensions={['.atlas']}
            pickerTitle="挑選圖集檔 (.atlas)"
            onBundleUpdated={onBundleUpdated}
            {...form.getInputProps('atlasFile')}
          />
        </Group>

        <Divider my="xs" label="各狀態多組加權隨機動作設定" labelPosition="center" />

        {SPINE_STATES.map((stateMeta) => {
          const config = (form.values[stateMeta.key] || {
            strategy: stateMeta.defaultStrategy,
            items: [],
          }) as StateGroupConfig;
          const itemsWithWeights = calculateNormalizedWeights(config.items || []);
          const isMulti = config.items && config.items.length > 1;

          return (
            <Paper key={stateMeta.key} withBorder p="xs" radius="sm" bg="var(--mantine-color-gray-0)">
              <Stack gap="xs">
                <Group justify="space-between" align="center">
                  <Group gap="xs">
                    <Text size="xs" fw={600}>
                      {stateMeta.label}
                    </Text>
                    {isMulti && (
                      <Badge size="xs" variant="light" color="indigo">
                        多動作隨機 ({config.items.length} 組)
                      </Badge>
                    )}
                  </Group>

                  <Group gap="xs">
                    <Group gap={4} align="center">
                      <SegmentedControl
                        size="xs"
                        value={config.strategy || stateMeta.defaultStrategy}
                        onChange={(val) => handleStrategyChange(stateMeta.key, val as RandomSelectionStrategy)}
                        data={[
                          { label: '循環隨機輪播', value: 'on-complete' },
                          { label: '進入狀態隨機', value: 'on-state-enter' },
                        ]}
                      />
                      <Tooltip
                        multiline
                        w={280}
                        withArrow
                        withinPortal
                        position="top-end"
                        label={
                          <Stack gap={4}>
                            <Text size="xs" fw={700}>
                              隨機選擇策略說明：
                            </Text>
                            <Text size="xs">
                              • <b>循環隨機輪播</b>：當動作播放完一輪（或單次動作結束）後，自動依權重隨機抽取下一個動作交替播放（適合多組待機/說話動作自然變換）。
                            </Text>
                            <Text size="xs">
                              • <b>進入狀態隨機</b>：僅在進入該狀態（例如從待機切換到思考中、或觸發點擊）時隨機抽取一次動作，狀態期間持續播放該動作不中途切換。
                            </Text>
                          </Stack>
                        }
                      >
                        <ActionIcon size="xs" variant="subtle" color="gray" aria-label="策略說明">
                          <IconHelpCircle size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>

                    <Button
                      size="compact-xs"
                      variant="light"
                      leftSection={<IconPlus size={12} />}
                      onClick={() => handleAddItem(stateMeta.key, stateMeta.defaultAnim, stateMeta.defaultLoop)}
                    >
                      新增動作
                    </Button>
                  </Group>
                </Group>

                {config.items.length === 0 ? (
                  <Box p="xs" ta="center">
                    <Text size="xs" c="dimmed" mb={4}>
                      尚未設定動作，點擊右上方「新增動作」以建立
                    </Text>
                  </Box>
                ) : (
                  <Stack gap={6}>
                    {config.items.map((item, idx) => {
                      const weightInfo = itemsWithWeights[idx];
                      const percentage = weightInfo?.percentage ?? 100;

                      return (
                        <Group key={idx} align="center" wrap="nowrap" gap="xs">
                          <Autocomplete
                            style={{ flex: 1 }}
                            size="xs"
                            placeholder="動作名稱 (例如: idle)"
                            data={availableSpineAnimations}
                            value={item.animation || ''}
                            onChange={(val) => handleUpdateItem(stateMeta.key, idx, { animation: val })}
                          />

                          <NumberInput
                            size="xs"
                            w={85}
                            min={1}
                            step={1}
                            placeholder="權重"
                            value={item.weight ?? 1}
                            onChange={(val) =>
                              handleUpdateItem(stateMeta.key, idx, {
                                weight: typeof val === 'number' && val > 0 ? val : 1,
                              })
                            }
                            leftSection={<IconPercentage size={12} />}
                          />

                          {isMulti && (
                            <Badge size="xs" color="teal" variant="light" w={46} style={{ textAlign: 'center' }}>
                              {percentage}%
                            </Badge>
                          )}

                          <Tooltip label={item.loop !== false ? '循環播放 (Looping)' : '單次播放 (One-shot)'}>
                            <Switch
                              size="xs"
                              label="循環"
                              checked={item.loop !== false}
                              onChange={(e) =>
                                handleUpdateItem(stateMeta.key, idx, { loop: e.currentTarget.checked })
                              }
                            />
                          </Tooltip>

                          <ActionIcon
                            size="sm"
                            color="red"
                            variant="subtle"
                            onClick={() => handleRemoveItem(stateMeta.key, idx)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Paper>
  );
}
